import * as Cesium from 'cesium';
import 'cesium/Build/Cesium/Widgets/widgets.css';

// Cesium ion token (optional). With a token we load Cesium World Terrain
// (echte 3D-Höhen: Schwäbische Alb, Schwarzwald, Alpen am Horizont);
// without it the app degrades to the flat globe.
const ionToken = import.meta.env.VITE_CESIUM_ION_TOKEN;
if (ionToken) {
  Cesium.Ion.defaultAccessToken = ionToken;
} else {
  Cesium.Ion.defaultAccessToken = undefined;
}

// Basiskarten von MapTiler (hochwertiger Google/Apple-Standard-Look, hell UND
// dunkel aus einer Hand → einheitliches Bild). Style-Paar "Base": base-v4 /
// base-v4-dark — aufgeräumt und gut lesbar. Für einen anderen Look genügt es,
// die Style-ID unten zu tauschen (z. B. streets-v2/-dark, dataviz/-dark).
//
// Bewusst der 256er-Endpoint (…/<style>/256/{z}/{x}/{y}.png): dadurch wählt
// Cesium für dieselbe Bildschirmfläche eine höhere Zoomstufe → kleinere
// Ortsnamen erscheinen schon weiter weggezoomt (bessere Orientierung). Der
// 512er-Endpoint würde die Auswahl eine Stufe gröber machen und blendet in der
// Übersicht fast alle Stadtnamen aus.
//
// Der API-Key steckt clientseitig in der URL (bei Karten-APIs üblich) — im
// MapTiler-Dashboard unbedingt auf die eigene(n) Domain(s) beschränken.
// Beide Themes werden vom Service Worker gecacht und vom Offline-Prefetch
// (data/tilePrefetch.js) vorgeladen — die URLs dort müssen identisch sein.
const maptilerKey = import.meta.env.VITE_MAPTILER_KEY;
if (!maptilerKey) {
  console.warn(
    'VITE_MAPTILER_KEY fehlt — die Basiskarte bleibt leer. Kostenlosen Key auf ' +
    'https://www.maptiler.com/ holen und in .env (Repo-Root) eintragen.'
  );
}
const MAPTILER_STYLE = { light: 'base-v4', dark: 'base-v4-dark' };
const maptilerUrl = (style) =>
  `https://api.maptiler.com/maps/${style}/256/{z}/{x}/{y}.png?key=${maptilerKey}`;

export const BASEMAPS = {
  light: maptilerUrl(MAPTILER_STYLE.light),
  dark: maptilerUrl(MAPTILER_STYLE.dark)
};

const BASEMAP_CREDIT = {
  light: '© MapTiler © OpenStreetMap contributors',
  dark: '© MapTiler © OpenStreetMap contributors'
};

// Tiefste verfügbare Zoomstufe (base-v4 liefert bis z22; 20 reicht für diese
// Nutzung und begrenzt die Kachelzahl).
const BASEMAP_MAX_LEVEL = { light: 20, dark: 20 };

const GLOBE_COLORS = {
  light: { base: '#e9ecef', background: '#dfe6ee' },
  dark: { base: '#1a1d26', background: '#101218' }
};

// MapTiler-Styles sind bereits sauber abgestimmt — nur ein Hauch Sättigung,
// sonst neutral lassen (der frühere kräftige Boost war reine Kompensation der
// blassen Esri-Kacheln). Feintuning nach Geschmack.
const BASEMAP_LOOK = {
  light: { saturation: 1.05, brightness: 1.0, contrast: 1.0 },
  dark: { saturation: 1.05, brightness: 1.0, contrast: 1.0 }
};

// (Früheres Esri-Hillshade-Overlay entfernt: MapTiler bringt eigene dezente
// Geländeschattierung mit; die echte 3D-Tiefe liefert das Cesium World Terrain.)

const AIRPORT_LABEL_COLORS = {
  light: { fill: '#1c2333', outline: '#ffffff' },
  dark: { fill: '#f2f4fa', outline: '#14161c' }
};

/**
 * Create and configure the Cesium viewer.
 * @returns {{ viewer, terrainEnabled, setBasemapTheme }}
 */
export async function createViewer(containerId, config, theme = 'light') {
  const viewer = new Cesium.Viewer(containerId, {
    baseLayerPicker: false,
    geocoder: false,
    homeButton: false,
    sceneModePicker: false,
    navigationHelpButton: false,
    animation: false,
    timeline: false,
    fullscreenButton: false,
    vrButton: false,
    selectionIndicator: false,
    infoBox: false,
    imageryProvider: false, // We'll add our own
    terrainProvider: undefined,
    contextOptions: {
      webgl: {
        alpha: true
      }
    }
  });

  let imageryLayer = null;
  let airportEntity = null;

  // Nur Zentraleuropa: Bounding-Box aus der Config (falls gesetzt) als
  // Cesium-Rectangle — begrenzt sowohl das Kachel-Laden als auch die Kamera.
  const b = config.region.bounds;
  const regionRectangle = b
    ? Cesium.Rectangle.fromDegrees(b.west, b.south, b.east, b.north)
    : undefined;

  function applyBasemap(nextTheme) {
    const url = BASEMAPS[nextTheme] || BASEMAPS.light;
    const colors = GLOBE_COLORS[nextTheme] || GLOBE_COLORS.light;

    const newLayer = viewer.imageryLayers.addImageryProvider(
      new Cesium.UrlTemplateImageryProvider({
        url,
        credit: BASEMAP_CREDIT[nextTheme] || BASEMAP_CREDIT.light,
        // 256er-Kacheln (MapTiler-256er-Endpoint): Cesium wählt damit eine
        // Stufe detaillierter → mehr/kleinere Ortsnamen schon in der Übersicht.
        tileWidth: 256,
        tileHeight: 256,
        maximumLevel: BASEMAP_MAX_LEVEL[nextTheme] ?? 20,
        // Nur Zentraleuropa laden: außerhalb dieser Box fordert Cesium keine
        // Kacheln an (spart MapTiler-Kontingent, hält den Fokus auf der Region).
        rectangle: regionRectangle
      })
    );
    const look = BASEMAP_LOOK[nextTheme] || BASEMAP_LOOK.light;
    newLayer.saturation = look.saturation;
    newLayer.brightness = look.brightness;
    newLayer.contrast = look.contrast;
    // Alte Ebene erst nach dem Hinzufügen entfernen (kein weißer Blitz)
    if (imageryLayer) {
      viewer.imageryLayers.remove(imageryLayer, true);
    }
    imageryLayer = newLayer;

    viewer.scene.globe.baseColor = Cesium.Color.fromCssColorString(colors.base);
    viewer.scene.backgroundColor = Cesium.Color.fromCssColorString(colors.background);

    if (airportEntity) {
      const labelColors = AIRPORT_LABEL_COLORS[nextTheme] || AIRPORT_LABEL_COLORS.light;
      airportEntity.label.fillColor = Cesium.Color.fromCssColorString(labelColors.fill);
      airportEntity.label.outlineColor = Cesium.Color.fromCssColorString(labelColors.outline);
    }

    viewer.scene.requestRender();
  }

  applyBasemap(theme);

  // Ensure globe is visible and rendering
  viewer.scene.globe.show = true;
  viewer.scene.skyBox = undefined;

  // Sanfte Atmosphäre + Nebel am Horizont (Apple-Maps-Look): gibt der
  // gekippten Ansicht Tiefe und beschleunigt nebenbei das Terrain-Rendering
  // (weit entfernte Kacheln werden gröber geladen).
  viewer.scene.skyAtmosphere.show = true;
  viewer.scene.fog.enabled = true;
  viewer.scene.fog.density = 0.0004;
  viewer.scene.globe.showGroundAtmosphere = true;
  viewer.scene.globe.enableLighting = false;

  // 3D-Terrain (nur mit ion-Token; graceful degradation zum flachen Globus)
  let terrainEnabled = false;
  if (ionToken) {
    try {
      viewer.terrainProvider = await Cesium.createWorldTerrainAsync();
      terrainEnabled = true;
      console.log('Cesium World Terrain enabled');
    } catch (error) {
      console.warn('Failed to load terrain, using flat globe:', error.message);
      viewer.terrainProvider = new Cesium.EllipsoidTerrainProvider();
      terrainEnabled = false;
    }
  }

  // Keep billboards/labels visible behind hills (they already disable depth test)
  viewer.scene.globe.depthTestAgainstTerrain = false;

  // Flüssigeres Karten-Erlebnis beim Zoomen/Schwenken: großzügiger
  // Kachel-Cache (weniger Neuladen beim Zurückzoomen) und Nachbarkacheln
  // schon im Hintergrund vorladen (Flightradar24-artiges "smoothes" Laden).
  viewer.scene.globe.tileCacheSize = 600;
  viewer.scene.globe.preloadSiblings = true;

  // --- Kamerabedienung im Google-Maps-Stil -----------------------------------
  // Links ziehen = verschieben, Rechts/Ctrl ziehen = neigen & rotieren,
  // Mausrad/Pinch = zoomen. Höhere Trägheit = weiches Ausgleiten der Karte.
  const controller = viewer.scene.screenSpaceCameraController;
  controller.inertiaSpin = 0.95;
  controller.inertiaTranslate = 0.95;
  controller.inertiaZoom = 0.92;
  // Zoomgeschwindigkeit: Cesiums Standard (zoomFactor 5, maxRatio 0.1) fühlt
  // sich v.a. beim Pinch-Zoom auf dem Handy zäh an — man muss mehrfach
  // kneifen, um näher zu kommen. Auf Touch-Geräten (pointer: coarse) drehen
  // wir deutlich auf; am Desktop nur moderat (Mausrad sonst zu sprunghaft).
  const coarsePointer = window.matchMedia?.('(pointer: coarse)')?.matches ?? false;
  controller.zoomFactor = coarsePointer ? 16 : 7;
  controller.maximumMovementRatio = coarsePointer ? 0.35 : 0.1;
  controller.zoomEventTypes = [
    Cesium.CameraEventType.WHEEL,
    Cesium.CameraEventType.PINCH
  ];
  controller.tiltEventTypes = [
    Cesium.CameraEventType.RIGHT_DRAG,
    Cesium.CameraEventType.MIDDLE_DRAG,
    Cesium.CameraEventType.PINCH,
    { eventType: Cesium.CameraEventType.LEFT_DRAG, modifier: Cesium.KeyboardEventModifier.CTRL }
  ];
  // Nicht unter das Gelände oder in den Weltraum zoomen
  controller.minimumZoomDistance = 250;
  controller.maximumZoomDistance = 1_500_000;
  controller.enableCollisionDetection = true;

  // Kamera in Zentraleuropa halten: wandert der Blickpunkt aus der Box, sanft
  // zurückschieben. Ergänzt die auf die Region begrenzten Kacheln, damit man
  // nicht in leere (kachellose) Bereiche schwenkt.
  if (regionRectangle) {
    viewer.camera.moveEnd.addEventListener(() => {
      const carto = viewer.camera.positionCartographic;
      const lon = Cesium.Math.clamp(carto.longitude, regionRectangle.west, regionRectangle.east);
      const lat = Cesium.Math.clamp(carto.latitude, regionRectangle.south, regionRectangle.north);
      if (lon !== carto.longitude || lat !== carto.latitude) {
        viewer.camera.setView({
          destination: Cesium.Cartesian3.fromRadians(lon, lat, carto.height),
          orientation: {
            heading: viewer.camera.heading,
            pitch: viewer.camera.pitch,
            roll: viewer.camera.roll
          }
        });
      }
    });
  }

  // Kamerafolgendes Licht statt Sonnenstand: die 3D-Flugzeugmodelle sind
  // damit auch nachts gleichmäßig ausgeleuchtet — die Basiskarte bleibt
  // unberührt (enableLighting ist aus).
  viewer.scene.light = new Cesium.DirectionalLight({
    direction: Cesium.Cartesian3.clone(viewer.camera.directionWC)
  });
  viewer.scene.preRender.addEventListener(() => {
    viewer.scene.light.direction = Cesium.Cartesian3.clone(
      viewer.camera.directionWC,
      viewer.scene.light.direction
    );
  });

  // Force immediate render
  viewer.scene.requestRender();

  // Set initial camera position centered on Stuttgart Airport
  const { center, initialAltitude } = config.region;
  viewer.camera.setView({
    destination: Cesium.Cartesian3.fromDegrees(
      center.lon,
      center.lat,
      initialAltitude
    ),
    orientation: {
      heading: Cesium.Math.toRadians(0),
      pitch: Cesium.Math.toRadians(-90),
      roll: 0
    }
  });

  // Add Stuttgart Airport marker (clamped to terrain)
  const labelColors = AIRPORT_LABEL_COLORS[theme] || AIRPORT_LABEL_COLORS.light;
  airportEntity = viewer.entities.add({
    name: config.airport.name,
    position: Cesium.Cartesian3.fromDegrees(config.airport.lon, config.airport.lat, 0),
    point: {
      pixelSize: 12,
      color: Cesium.Color.fromCssColorString('#1a73e8'),
      outlineColor: Cesium.Color.WHITE,
      outlineWidth: 2,
      heightReference: Cesium.HeightReference.CLAMP_TO_GROUND,
      disableDepthTestDistance: Number.POSITIVE_INFINITY
    },
    label: {
      text: config.airport.code,
      font: '600 14px sans-serif',
      fillColor: Cesium.Color.fromCssColorString(labelColors.fill),
      outlineColor: Cesium.Color.fromCssColorString(labelColors.outline),
      outlineWidth: 3,
      style: Cesium.LabelStyle.FILL_AND_OUTLINE,
      verticalOrigin: Cesium.VerticalOrigin.TOP,
      pixelOffset: new Cesium.Cartesian2(0, 15),
      heightReference: Cesium.HeightReference.CLAMP_TO_GROUND,
      disableDepthTestDistance: Number.POSITIVE_INFINITY
    }
  });

  // Optimize rendering
  viewer.scene.requestRenderMode = true;
  viewer.scene.maximumRenderTimeChange = 0.1;

  console.log('Cesium viewer initialized');
  return { viewer, terrainEnabled, setBasemapTheme: applyBasemap };
}
